import { Box, Paper, Stack, Typography } from "@mui/material"
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined"
import type { ImagemProduto } from "@/api"
import { capitalize, formatarBRL } from "@/lib/formato"
import { urlImagemProduto } from "@/lib/imagem"
import QuantityStepper from "./quantity-stepper"

/** Lado da miniatura no card. Ao ImageBoss pedimos o dobro, por causa da tela retina. */
const LADO_MINIATURA = 72

interface StepOptionCardProps {
    nome: string
    descricao?: string | null
    /** Foto da opção. `null` = o produto não tem foto cadastrada. */
    imagem?: ImagemProduto | null
    /** Reserva o espaço da miniatura mesmo sem foto — decidido por passo, para a lista não desalinhar. */
    mostrarImagem?: boolean
    valor: number
    quantidade: number
    maxAtingido: boolean
    onAdd: () => void
    onRemove: () => void
}

function StepOptionCard({ nome, descricao, imagem, mostrarImagem = false, valor, quantidade, maxAtingido, onAdd, onRemove }: StepOptionCardProps): JSX.Element {
    const selecionado = quantidade > 0
    const texto = descricao?.trim()
    const urlImagem = urlImagemProduto(imagem, {
        largura: LADO_MINIATURA * 2,
        altura: LADO_MINIATURA * 2,
        modo: 'cover',
    })

    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                borderRadius: 3,
                border: '1.5px solid',
                borderColor: selecionado ? 'primary.main' : 'divider',
                transition: 'border-color 0.2s',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={2}>
                {mostrarImagem && (
                    <Box
                        sx={{
                            width: LADO_MINIATURA,
                            height: LADO_MINIATURA,
                            flexShrink: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'action.hover',
                        }}
                    >
                        {urlImagem ? (
                            <Box
                                component="img"
                                src={urlImagem}
                                alt={nome}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 28, opacity: 0.24 }} />
                        )}
                    </Box>
                )}

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        {capitalize(nome)}
                    </Typography>

                    {texto && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {texto}
                        </Typography>
                    )}

                    <Typography variant="body1" color={valor > 0 ? 'text.secondary' : 'success.main'} sx={{ mt: 0.25 }}>
                        {valor > 0 ? `+ ${formatarBRL(valor)}` : 'Sem custo'}
                    </Typography>
                </Box>

                <QuantityStepper
                    quantidade={quantidade}
                    maxAtingido={maxAtingido}
                    onAdd={onAdd}
                    onRemove={onRemove}
                />
            </Stack>
        </Paper>
    )
}

export default StepOptionCard